import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import PortalHeader from '../PortalHeader';
import { useGuide } from './GuideContext';
import { GUIDE_GROUPS, GUIDE_TOPICS, stepsFor } from './guideTopics';
import '../portalSurface.css';
import './HelpPage.css';

// "How do I…": the staff guide as a page. Each topic is a card with its
// steps (opened with a labelled toggle, never hover) and a Show me button
// that starts the guided tour on the real page.
//
// Rendered at /portal/work/help for admins (sidebar) and as the Help tab on a
// teacher's Dashboard, so it must not assume either chrome.
export default function HelpPage() {
	const guide = useGuide();
	const { userProfile } = useAuth();
	const isAdmin = guide ? guide.isAdmin : userProfile?.role === 'admin';
	const [open, setOpen] = useState(() => new Set());

	const toggle = (id) =>
		setOpen((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	return (
		<div className='ps-page help-page'>
			<PortalHeader
				title='How do I…'
				subtitle='Short guides to the things you do every day. Show me walks you through it on the real page, one step at a time.'
			/>

			{GUIDE_GROUPS.map((group) => {
				const topics = GUIDE_TOPICS.filter((t) => t.group === group.key);
				if (!topics.length) return null;
				return (
					<section className='help-group' key={group.key} aria-labelledby={`help-group-${group.key}`}>
						<h2 className='help-group__title' id={`help-group-${group.key}`}>
							<i className={`fa-sharp fa-solid ${group.icon}`} aria-hidden='true' />
							{group.title}
						</h2>
						<div className='help-group__list'>
							{topics.map((topic) => {
								const steps = stepsFor(topic, isAdmin);
								const isOpen = open.has(topic.id);
								return (
									<article className='ps-card help-topic' key={topic.id}>
										<div className='help-topic__head'>
											<div className='help-topic__words'>
												<h3 className='help-topic__title'>{topic.title}</h3>
												<p className='help-topic__summary'>{topic.summary}</p>
											</div>
											<div className='help-topic__actions'>
												{guide && (
													<button
														type='button'
														className='ps-btn ps-btn--primary help-topic__btn'
														onClick={() => guide.start(topic.id)}>
														<i className='fa-sharp fa-solid fa-signs-post' aria-hidden='true' />
														Show me
													</button>
												)}
												<button
													type='button'
													className='ps-btn help-topic__btn'
													aria-expanded={isOpen}
													aria-controls={`help-steps-${topic.id}`}
													onClick={() => toggle(topic.id)}>
													<i
														className={`fa-sharp fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
														aria-hidden='true'
													/>
													{isOpen ? 'Hide steps' : 'Read the steps'}
												</button>
											</div>
										</div>
										{isOpen && (
											<ol className='help-topic__steps' id={`help-steps-${topic.id}`}>
												{steps.map((s, i) => (
													<li key={i}>{s.text}</li>
												))}
											</ol>
										)}
									</article>
								);
							})}
						</div>
					</section>
				);
			})}

			<p className='help-page__foot'>
				<i className='fa-sharp fa-solid fa-circle-question' aria-hidden='true' />
				<span>
					Something here does not match what you see, or a step does not work? Please tell
					Anthony right away.
				</span>
			</p>
		</div>
	);
}
